import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../../api/client'
import type { Trade, TrackRecordSignal } from '../../api/types'
import { Card, PageHead, Seg, Tabs, Tag, inr, signed, toneClass } from '../ui'
import { usePlan } from '../../lib/plan'
import { EvidencePanelLive } from '../beginner/EvidencePanel'
import { BrainProgressLive, InsightsLive } from '../beginner/InsightsPanel'
import { SCORE_HINT, scoreLabel, scoreLabelLong } from '../scoreText'

const TABS = ['Trade history', "Version 1's picks", 'Strategy results'] as const
type TabName = (typeof TABS)[number]

// Plain one-liners for the strategies we know about. Anything else falls back
// to the code name with underscores removed — never an invented description.
const STRATEGY_PLAIN: Record<string, string> = {
  ml_swing_main: 'Large companies (model)',
  ml_swing_midcap: 'Mid-size companies (model)',
  ml_swing_smallcap: 'Small companies (model)',
  sma_crossover_benchmark: 'Simple yardstick (moving averages)',
  sma_crossover: 'Simple yardstick (moving averages)',
  brain: 'TradeMind brain (practice)',
  real_trading: 'Your own buys',
  long_term_value: 'Long-term picks',
}

const strategyPlain = (name: string | null) =>
  name ? (STRATEGY_PLAIN[name] ?? name.replace(/_/g, ' ')) : 'Unknown'

const money = (n: number) => `${n < 0 ? '−' : ''}₹${inr(Math.abs(n))}`
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })

function Loading() {
  return <p className="tm-dim">Loading…</p>
}

function Problem({ what }: { what: string }) {
  return <p className="tm-dim">Could not load {what} right now. Try again shortly.</p>
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="tm-stat-label">{label}</div>
      <div className={`tm-stat-value ${tone ?? ''}`}>{value}</div>
    </div>
  )
}

// ---------------------------------------------------------- trade history --

interface PeriodRow {
  key: string
  label: string
  trades: number
  wins: number
  net: number
  charges: number
}

function TradeHistory() {
  const [period, setPeriod] = useState<'month' | 'year'>('month')
  // Same call the old Reports page makes: the bot's own book, up to the
  // API's ceiling of 1000, newest first.
  const q = useQuery({ queryKey: ['trades', 1000], queryFn: () => api.trades(1000) })

  const rows = useMemo(
    () => [...(q.data ?? [])].sort((a, b) => new Date(b.exit_at).getTime() - new Date(a.exit_at).getTime()),
    [q.data],
  )
  const byStrategy = useMemo(() => {
    const m = new Map<string, { key: string; trades: number; wins: number; net: number }>()
    for (const t of rows) {
      const key = t.strategy_name ?? 'Unknown'
      const r = m.get(key) ?? { key, trades: 0, wins: 0, net: 0 }
      r.trades += 1
      r.wins += t.is_win ? 1 : 0
      r.net += t.net_pnl
      m.set(key, r)
    }
    return [...m.values()].sort((a, b) => b.net - a.net)
  }, [rows])

  const byPeriod = useMemo(() => {
    const m = new Map<string, PeriodRow>()
    for (const t of rows) {
      const d = new Date(t.exit_at)
      const y = d.getFullYear()
      const key = period === 'year' ? `${y}` : `${y}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label =
        period === 'year' ? `${y}` : d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
      const r = m.get(key) ?? { key, label, trades: 0, wins: 0, net: 0, charges: 0 }
      r.trades += 1
      r.wins += t.is_win ? 1 : 0
      r.net += t.net_pnl
      r.charges += t.charges
      m.set(key, r)
    }
    return [...m.values()].sort((a, b) => b.key.localeCompare(a.key))
  }, [rows, period])

  if (q.isLoading) return <Loading />
  if (q.isError) return <Problem what="your trade history" />
  if (!rows.length) {
    return (
      <Card title="Trade history">
        <p className="tm-dim">No finished trades yet. Each trade will show up here once the bot has sold it.</p>
      </Card>
    )
  }

  const wins = rows.filter((t: Trade) => t.is_win).length
  const net = rows.reduce((s, t) => s + t.net_pnl, 0)
  const charges = rows.reduce((s, t) => s + t.charges, 0)
  // Made money before fees and taxes, but not after.
  const feesAte = rows.filter((t) => t.gross_pnl > 0 && t.net_pnl <= 0).length
  // Rose 5% or more in the weeks after we sold.
  const rose = rows.filter((t) => (t.return_15d_after_exit ?? t.return_5d_after_exit ?? 0) >= 0.05).length

  return (
    <div className="tm-grid">
      {feesAte > 0 && (
        <Card>
          <p>
            <Tag tone="amber">Heads up</Tag> Fees and taxes took all of the profit on {feesAte}{' '}
            {feesAte === 1 ? 'trade' : 'trades'}. They made money before costs, but ended at zero or a loss.
          </p>
        </Card>
      )}
      {rose > 0 && (
        <Card>
          <p>
            <Tag tone="amber">Heads up</Tag> {rose} {rose === 1 ? 'stock' : 'stocks'} went up by 5% or more in
            the weeks after we sold {rose === 1 ? 'it' : 'them'}. Worth checking whether we sold too early. See
            the "Moved after selling" columns below.
          </p>
        </Card>
      )}
      <Card title="Overall" sub="Every finished trade, after all costs">
        <div className="tm-grid tm-cols-4">
          <Stat label="Finished trades" value={String(rows.length)} />
          <Stat label="Made money" value={String(wins)} tone="tm-pos" />
          <Stat label="Lost money" value={String(rows.length - wins)} tone={rows.length - wins > 0 ? 'tm-neg' : ''} />
          <Stat label="Total result" value={money(net)} tone={toneClass(net)} />
        </div>
        <p className="tm-dim" style={{ marginTop: '0.6rem' }}>
          Fees and taxes paid on these trades: {money(charges)}.
        </p>
      </Card>

      <Card
        title={period === 'month' ? 'By month' : 'By year'}
        sub="Trades grouped by the date they were sold"
        action={
          <Seg
            small
            options={[
              { id: 'month', label: 'By month' },
              { id: 'year', label: 'By year' },
            ]}
            active={period}
            onChange={setPeriod}
          />
        }
      >
        <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
          <table className="tm-table">
            <thead>
              <tr>
                <th>{period === 'month' ? 'Month' : 'Year'}</th>
                <th className="tm-right">Trades</th>
                <th className="tm-right">Made money</th>
                <th className="tm-right">Lost money</th>
                <th className="tm-right">Result</th>
                <th className="tm-right">Fees and taxes</th>
              </tr>
            </thead>
            <tbody>
              {byPeriod.map((r) => (
                <tr key={r.key}>
                  <td>
                    <strong>{r.label}</strong>
                  </td>
                  <td className="tm-right">{r.trades}</td>
                  <td className="tm-right">{r.wins}</td>
                  <td className="tm-right">{r.trades - r.wins}</td>
                  <td className={`tm-right ${toneClass(r.net)}`}>{money(r.net)}</td>
                  <td className="tm-right tm-dim">{money(r.charges)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {byStrategy.length > 1 && (
        <Card title="Result by strategy" sub="Which approach is earning, and which is not">
          <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
            <table className="tm-table">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th className="tm-right">Trades</th>
                  <th className="tm-right">Made money</th>
                  <th className="tm-right">Result</th>
                </tr>
              </thead>
              <tbody>
                {byStrategy.map((r) => (
                  <tr key={r.key}>
                    <td title={r.key}>{strategyPlain(r.key)}</td>
                    <td className="tm-right">{r.trades}</td>
                    <td className="tm-right">{r.wins}</td>
                    <td className={`tm-right ${toneClass(r.net)}`}>{money(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Every finished trade" sub="Newest first">
        <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Stock</th>
                <th>Bought</th>
                <th>Sold</th>
                <th className="tm-right">Shares</th>
                <th className="tm-right">Bought at</th>
                <th className="tm-right">Sold at</th>
                <th className="tm-right">Result</th>
                <th className="tm-right">Result %</th>
                <th>Why it was sold</th>
                <th className="tm-right" title="Price move 5 trading days after we sold">
                  Moved after selling (5 days)
                </th>
                <th className="tm-right" title="Price move 15 trading days after we sold">
                  Moved after selling (15 days)
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td title={t.strategy_name ?? ''}>
                    <strong>{t.symbol}</strong>
                    {t.gross_pnl > 0 && t.net_pnl <= 0 && (
                      <span title="Made money before fees and taxes, but they took all of it"> ⚠</span>
                    )}
                  </td>
                  <td>{day(t.entry_at)}</td>
                  <td>{day(t.exit_at)}</td>
                  <td className="tm-right">{t.quantity}</td>
                  <td className="tm-right">₹{inr(t.entry_price)}</td>
                  <td className="tm-right">₹{inr(t.exit_price)}</td>
                  <td className={`tm-right ${toneClass(t.net_pnl)}`}>{money(t.net_pnl)}</td>
                  <td className={`tm-right ${toneClass(t.net_pnl)}`}>{signed(t.return_pct * 100)}</td>
                  <td className="tm-wrap" title={t.exit_reason ?? ''}>
                    {t.exit_reason_label ?? t.exit_reason ?? '—'}
                  </td>
                  <td className="tm-right">
                    {t.return_5d_after_exit != null ? signed(t.return_5d_after_exit * 100) : '—'}
                  </td>
                  <td className="tm-right">
                    {t.return_15d_after_exit != null ? signed(t.return_15d_after_exit * 100) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ------------------------------------------------------- version 1's picks --

const OUTCOME: Record<string, { label: string; tone: 'green' | 'red' | 'amber' }> = {
  TARGET_HIT: { label: 'Reached its goal', tone: 'green' },
  STOP_LOSS_HIT: { label: 'Fell to its safety price', tone: 'red' },
  EXPIRED_NO_HIT: { label: 'Time ran out', tone: 'amber' },
}

function ScanTable({ rows }: { rows: TrackRecordSignal[] }) {
  const [open, setOpen] = useState<number | null>(null)
  return (
    <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
      <table className="tm-table">
        <thead>
          <tr>
            <th>Stock</th>
            <th>Picked on</th>
            <th className="tm-right">Price then</th>
            <th className="tm-right">Price now</th>
            <th className="tm-right">Safety price</th>
            <th className="tm-right">Goal price</th>
            <th>How it went</th>
            <th className="tm-right">Result</th>
            <th>Why it was picked</th>
            <th>More</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const o = r.outcome ? OUTCOME[r.outcome] : null
            const pct = r.was_executed && r.trade_return_pct != null ? r.trade_return_pct : r.outcome_pct
            return (
              <Fragment key={r.signal_id}>
              <tr>
                <td title={r.strategy_name}>
                  <strong>{r.symbol}</strong>
                  {r.name && <div className="tm-dim">{r.name}</div>}
                </td>
                <td>
                  {day(r.generated_at)}
                  <div className="tm-dim">{r.age_days} days ago</div>
                </td>
                <td className="tm-right">₹{inr(r.price)}</td>
                <td className="tm-right">{r.current_price != null ? `₹${inr(r.current_price)}` : '—'}</td>
                <td className="tm-right">₹{inr(r.stop_loss)}</td>
                <td className="tm-right">₹{inr(r.take_profit)}</td>
                <td>
                  {r.sealed ? (
                    <span title="Sealed until the trial's evaluation (blind test): counts only.">
                      <Tag tone="violet">Sealed</Tag>
                    </span>
                  ) : o ? (
                    <Tag tone={o.tone}>{o.label}</Tag>
                  ) : (
                    <Tag tone="blue">Still open</Tag>
                  )}
                </td>
                <td className={`tm-right ${pct != null ? toneClass(pct) : 'tm-dim'}`}>
                  {pct != null ? signed(pct * 100) : '—'}
                </td>
                <td className="tm-wrap">{r.reason ?? '—'}</td>
                <td>
                  <button className="tm-btn" onClick={() => setOpen(open === r.signal_id ? null : r.signal_id)}>
                    {open === r.signal_id ? 'Hide' : 'Details'}
                  </button>
                </td>
              </tr>
              {open === r.signal_id && (
                <tr>
                  <td colSpan={10} className="tm-wrap">
                    <div>Strategy: {strategyPlain(r.strategy_name)}</div>
                    <div title={SCORE_HINT}>{scoreLabelLong(r.confidence)}</div>
                    <div>
                      {r.was_executed
                        ? `We bought this one. Actual result: ${
                            r.trade_net_pnl != null ? money(r.trade_net_pnl) : 'not finished yet'
                          }${r.trade_return_pct != null ? ` (${signed(r.trade_return_pct * 100)})` : ''}`
                        : 'We did not buy this one.'}
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Picks() {
  const buy = useQuery({ queryKey: ['buyList'], queryFn: api.buyList })
  const scan = useQuery({ queryKey: ['scanResults'], queryFn: api.scanResults })
  const buyScores = (buy.data ?? []).map((r) => r.confidence)

  return (
    <div className="tm-grid">
      <p className="tm-dim">
        This is the original model's list (version 1). It is separate from the brain's ideas on the
        Opportunities page.
      </p>

      <Card title="Today's buy list" sub="Stocks the original model would buy right now">
        {buy.isLoading && <Loading />}
        {buy.isError && <Problem what="the buy list" />}
        {buy.data && buy.data.length === 0 && (
          <p className="tm-dim">Nothing on the buy list today. The model found no stock worth buying.</p>
        )}
        {buy.data && buy.data.length > 0 && (
          <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
            <table className="tm-table">
              <thead>
                <tr>
                  <th>Stock</th>
                  <th className="tm-right">Price</th>
                  <th className="tm-right">Sell if it falls to</th>
                  <th className="tm-right">Goal price</th>
                  <th className="tm-right" title={SCORE_HINT}>
                    Model score rank
                  </th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {buy.data.map((r) => (
                  <tr key={`${r.symbol}-${r.strategy_name}`}>
                    <td title={r.strategy_name}>
                      <strong>{r.symbol}</strong>
                      {r.name && <div className="tm-dim">{r.name}</div>}
                    </td>
                    <td className="tm-right">₹{inr(r.price)}</td>
                    <td className="tm-right">{r.stop_loss != null ? `₹${inr(r.stop_loss)}` : '—'}</td>
                    <td className="tm-right">{r.take_profit != null ? `₹${inr(r.take_profit)}` : '—'}</td>
                    <td className="tm-right">{scoreLabel(r.confidence, buyScores)}</td>
                    <td className="tm-wrap">{r.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="What the daily scan found" sub="Every pick so far, and how it turned out">
        {scan.isLoading && <Loading />}
        {scan.isError && <Problem what="the scan results" />}
        {scan.data && scan.data.length === 0 && (
          <p className="tm-dim">The daily scan has not made any picks yet.</p>
        )}
        {scan.data && scan.data.length > 0 && <ScanTable rows={scan.data} />}
      </Card>
    </div>
  )
}

// -------------------------------------------------------- strategy results --

function StrategyResults() {
  const q = useQuery({ queryKey: ['strategyPerformance'], queryFn: api.strategyPerformance })

  if (q.isLoading) return <Loading />
  if (q.isError) return <Problem what="the strategy results" />
  const list = q.data?.strategies ?? []
  if (!list.length) {
    return (
      <Card title="Strategy results">
        <p className="tm-dim">No strategies have any results yet.</p>
      </Card>
    )
  }

  return (
    <div className="tm-grid">
      <p className="tm-dim">
        A strategy is one way of picking stocks. Each is tried on its own so you can see which works best.
        This page is for reading only.
      </p>
      {list.map((s) => {
        const w = [
          { label: 'Last 30 days', v: s.windows.last_30d },
          { label: 'Last 90 days', v: s.windows.last_90d },
          { label: 'Since the start', v: s.windows.all_time },
        ]
        return (
          <Card
            key={s.id}
            title={<span title={s.name}>{strategyPlain(s.name)}</span>}
            sub={`Holding ${s.open_positions} now`}
            action={<Tag tone={s.is_active ? 'green' : 'amber'}>{s.is_active ? 'Running' : 'Paused'}</Tag>}
          >
            <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
              <table className="tm-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="tm-right">Finished trades</th>
                    <th className="tm-right">Made money</th>
                    <th className="tm-right">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {w.map(({ label, v }) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className="tm-right">{v.trades}</td>
                      <td className="tm-right">{v.trades ? `${(v.win_rate * 100).toFixed(0)}%` : '—'}</td>
                      <td className={`tm-right ${v.trades ? toneClass(v.net_pnl) : 'tm-dim'}`}>
                        {v.trades ? money(v.net_pnl) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

// bx4: is it working so far? Practice money only; the brain's trial is a count until its review (sealed). The bot's
// own book is the owner's, so the server refuses this to everyone else and it is not shown to them.
function Evidence() {
  const { canManage } = usePlan()
  if (!canManage) return null
  return (
    <Card glow title="Is it working so far?" sub="Practice money (paper) only. No real money is counted here.">
      <EvidencePanelLive />
    </Card>
  )
}

// oa11: day / week / month results, practice and real side by side (never added together), against two
// benchmarks after costs; and whether the brain is getting better (counts and frozen tests only). Owner only,
// like the card above: the server refuses these figures to everyone else.
function Insights() {
  const { canManage } = usePlan()
  if (!canManage) return null
  return (
    <>
      <Card title="Day by day, week by week, month by month" sub="Practice money and real money are kept apart and never added together.">
        <InsightsLive />
      </Card>
      <Card title="Is the brain getting better?" sub="Counts and frozen tests only. The brain's trial results stay sealed until its review.">
        <BrainProgressLive />
      </Card>
    </>
  )
}

export default function Records() {
  const [tab, setTab] = useState<TabName>('Trade history')
  return (
    <div className="tm-page tm-grid">
      <PageHead title="Records" sub="Everything that has already happened, for reading only" />
      <Evidence />
      <Insights />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Trade history' && <TradeHistory />}
      {tab === "Version 1's picks" && <Picks />}
      {tab === 'Strategy results' && <StrategyResults />}
    </div>
  )
}
